import { useCallback, useEffect, useState } from "react";
import { api, errorMessage } from "../lib/api";

const PERIODS = [["today", "Oggi"], ["week", "Settimana"], ["month", "Mese"]];
const COLUMNS = [["assigned", "Assegnati"], ["worked", "Lavorati"], ["worked_pct", "% lavorati"],
  ["untouched", "Non toccati"], ["stalled", "Fermi"], ["calls", "Chiamate"],
  ["successful_contacts", "Contatti riusciti"], ["appointments", "Appuntamenti"],
  ["meetings_done", "Incontri fatti"], ["clients", "Clienti chiusi"],
  ["conversion_pct", "% a cliente"], ["monthly_value_cents", "Valore mensile"],
  ["missing_outcomes", "Esiti mancanti"], ["days_since_activity", "Giorni inattivi"]];
const SNAPSHOT = new Set(["assigned", "worked", "worked_pct", "untouched", "stalled", "days_since_activity"]);
const money = (cents) => `${((cents || 0) / 100).toLocaleString("it-IT", { minimumFractionDigits: 2 })} €`;
const format = (key, value) => key === "monthly_value_cents" ? money(value)
  : key.endsWith("_pct") ? `${value || 0}%` : value ?? "—";
const trend = (change, key) => { if (change === undefined || change === null) return null;
  return <small className={`team-trend${change > 0 ? " up" : change < 0 ? " down" : ""}`}
    data-testid={`team-trend-${key}`}>{change > 0 ? "↑" : change < 0 ? "↓" : "→"}{Math.abs(change)}</small>;
};

export const TeamStats = ({ onOpen }) => {
  const [period, setPeriod] = useState("today");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => api.get("/team/stats", { params: { period } }).then(({ data: result }) => setData(result))
    .catch((e) => setError(errorMessage(e))), [period]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const onVisible = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [load]);
  return <section className="team-stats" data-testid="team-stats">
    <div className="team-stats-heading"><h3 className="section-label">NUMERI DELLA SQUADRA</h3>
      <div className="team-periods" role="group" aria-label="Periodo statistiche">{PERIODS.map(([key, label]) =>
        <button key={key} type="button" className={period === key ? "active" : ""}
          aria-pressed={period === key} onClick={() => setPeriod(key)} data-testid={`team-period-${key}`}>{label}</button>)}</div></div>
    {error && <p role="alert" className="form-error" data-testid="team-stats-error">{error}</p>}
    <div className="team-stats-scroll" role="region" tabIndex={0} aria-label="Statistiche della squadra, scorri lateralmente"
      data-testid="team-stats-scroll"><table className="team-stats-table"><thead><tr><th scope="col">Persona</th>
      {COLUMNS.map(([key, label]) => <th scope="col" key={key} data-testid={`team-col-${key}`}>{label}</th>)}</tr></thead><tbody>
      {data?.rows.map((row) => <tr key={row.id} tabIndex={0} onClick={() => onOpen(row)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(row); } }}
        data-testid={`team-stats-row-${row.id}`}>
        <th scope="row" data-testid={`team-stats-name-${row.id}`}>{row.name}</th>
        {COLUMNS.map(([key]) => <td key={key} className={
          (key === "untouched" && row.untouched_overdue > 0) || (key === "stalled" && row.stalled > 0)
          || (key === "missing_outcomes" && row.missing_outcomes > 0)
          || (key === "days_since_activity" && row.days_since_activity >= 2) ? "stat-danger" : ""}
          data-testid={`team-stats-${row.id}-${key}`}>{format(key, row[key])}
          {!SNAPSHOT.has(key) && trend(row.delta?.[key], `${row.id}-${key}`)}</td>)}</tr>)}
      {data && <tr className="team-total" data-testid="team-stats-total"><th scope="row">Totale squadra</th>
        {COLUMNS.map(([key]) => <td key={key} data-testid={`team-total-${key}`}>
          {key === "days_since_activity" ? "—" : format(key, data.totals[key])}
          {!SNAPSHOT.has(key) && trend(data.total_delta?.[key], `total-${key}`)}</td>)}</tr>}
    </tbody></table></div>
    {data && <p className="team-unassigned" data-testid="team-unassigned-total">Da assegnare · <strong>{data.totals.unassigned}</strong></p>}
    {data && <div className="team-funnel" data-testid="team-funnel"><h3 className="section-label">IMBUTO</h3>
      {data.funnel.map((step) => <div className="team-funnel-step" key={step.key} data-testid={`funnel-${step.key}`}>
        <div><span>{{ calls: "Chiamate", successful_contacts: "Contatti", appointments: "Appuntamenti",
          meetings_done: "Incontri", clients: "Clienti" }[step.key]}</span><strong>{step.count} · {step.rate}%</strong></div>
        <div className="team-funnel-track"><i style={{ width: `${Math.min(100, step.rate)}%` }} /></div>
      </div>)}</div>}
  </section>;
};